import React, { useRef, useState, useEffect } from "react";
import LocatioPinIcon from "../../assets/icons/location-pin.png";
import { Star } from "lucide-react";
import StickyBox from "react-sticky-box";
import CustomButton from "../../components/Form/CustomButton";
import { useNavigate, useParams } from "react-router-dom";

import ReviewCard from "../../components/Client/TaskerProfileReviewCard";
import SlidingTabs from "../../components/Common/SlidingTabs";
import { useThunk } from "../../hooks/useThunk";
import { getAllOrders, getTaskerDetails } from "../../store";
import { useSelector } from "react-redux";
import ProfileImage from "../../components/Common/ProfileImage";
import Loader from "../../components/Common/Loader";
import ErrorDisplay from "../../error/ErrorDisplay";
import { createThread } from "../../store/thunks/threadThunks";
import { ErrorCodes } from "../../constants/errorCodes";
import showToast from "../../utils/toastNotifications";
import ServiceProCardPageSkeleton from "../../components/skeleton/ServiceProCardPageSkeleton";
import ReviewCardSkeleton from "../../components/skeleton/ReviewCardSkeleton";
import SkillCardSkeleton from "../../components/skeleton/SkillCardSkeleton";
const LIMIT = 5;

const ClientTaskerProfile = () => {
  const navigate = useNavigate();
  const { profile } = useSelector(
    (state) => state.user?.taskerProfile || { profile: {} }
  );
  const userState = useSelector((state) => state?.auth?.user);

  const { taskerId } = useParams();
  const reviewsRef = useRef(null);
  const sectionRef = useRef(null);

  const [reviews, setReviews] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalReviews, setTotalReviews] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeLocation, setActiveLocation] = useState(null);
  const [sectionHeight, setSectionHeight] = useState(0);
  const [isSticky, setIsSticky] = useState(false);

  //Mapping Tasker
  const category = profile?.professionalDetails?.primary || [];
  const serviceAreas = profile?.servingAreas?.primary || [];
  const subCities = activeLocation?.subcities || [];

  //Mapping Skills
  const skillsList =
    activeCategory?.skills?.map((skill) => ({
      name: skill.skillName,
      description: skill.description,
    })) || [];

  const [doFetchUser, isFetchingUser, userError] = useThunk(getTaskerDetails);
  const [doFetchReviews, isFetchingReviews, reviewError] =
    useThunk(getAllOrders);
  const [doCreateThread, isCreateThread, errorCreateThread] =
    useThunk(createThread);

  useEffect(() => {
    if (profile?.professionalDetails?.primary) {
      setActiveCategory(profile.professionalDetails.primary[0]);
    }
    if (profile?.servingAreas?.primary) {
      setActiveLocation(profile.servingAreas.primary[0]);
    }
  }, [profile]);

  //Load initial States
  useEffect(() => {
    if (taskerId) {
      loadServicePro();
      fetchReviews();
    }
  }, []);

  //Fetch Taskers
  const loadServicePro = async (page) => {
    const userId = taskerId;

    // withReviewedOrdersCount:true,
    // withTotalOrdersCount:true,

    try {
      await doFetchUser(userId);
    } catch (error) {
      console.error("Failed to fetch user profiles:", error);
    }
  };

  // Fetch reviews
  const fetchReviews = async () => {
    const response = await doFetchReviews({
      userId: taskerId,
      isReviews: true,
      limit: LIMIT,
      offset,
    });

    if (response?.success) {
      const orders = response.response.data.data;
      const pagination = response.response.data.pagination;

      const newReviews = orders.map((order) => ({
        review: order.userReview,
        price: order.price,
        userFirstName: order.user.firstName,
        userLastName: order.user.lastName,
        profilePicture: order.user.profilePicture,
        userRole: order.user.roles[0],
      }));
      setReviews((prev) => [...prev, ...newReviews]);
      setTotalReviews(pagination.totalDocuments);

      if (reviews.length + newReviews.length >= pagination.totalDocuments) {
        setHasMore(false);
      }
      setOffset((prev) => prev + LIMIT);
    }
  };

  // Update the height of the section dynamically
  useEffect(() => {
    if (sectionRef.current) {
      setSectionHeight(sectionRef.current.offsetHeight);
    }
  }, []);

  // Track scroll position and check sticky state
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const section = sectionRef.current;
        const sectionBottom = section.offsetTop + section.offsetHeight; // bottom of the section
        const scrollPosition = window.scrollY + window.innerHeight; // current scroll position + window height

        // If the scroll is at the bottom of the section, make it sticky
        if (scrollPosition >= sectionBottom) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  //Handle Load More Button
  const handleLoadMore = async () => {
    if (hasMore) {
      setOffset((prevOffset) => prevOffset + LIMIT); // Update offset for the next batch
      await fetchReviews(); // Fetch the next batch of reviews
    }
  };

  // createing a new chat between two unique users
  const handleStartChat = async (user2Id) => {
    if (user2Id) {
      const response = await doCreateThread(user2Id);
      if (response?.success) {
        navigate(`/chat?thread=${response?.response?.data?._id}`);
      } else {
        if (response?.error?.customCode === ErrorCodes.DUPLICATE_ENTRIES.code) {
          navigate(
            `/chat?thread=${response?.error?.message
              ?.split(":")[1]
              ?.toString()
              ?.trim()}`
          );
        } else {
          showToast("error", response?.error?.message || "Error Occured");
        }
      }
    } else {
      showToast("error", "User ID Not Provided");
    }
  };

  return (
    <div className="mt-6 sm:mt-8  md:mt-12 mb-6 sm:mb-8 md:mb-[88px] px-4 sm:px-6 md:px-[40px] lg:px-[100px] flex flex-col gap-6 sm:gap-8 md:gap-10">
      {/* Top Section */}
      <div className="border bg-white rounded-lg sm:rounded-[20px] border-light-blue p-4 sm:p-5 lg:p-9 flex flex-col gap-6 sm:gap-8 md:gap-12 xl:flex-row">
        {isFetchingUser || isFetchingReviews ? (
          <div className=" flex flex-col w-full gap-6 min-h-[200px]">
            <ServiceProCardPageSkeleton />
          </div>
        ) : userError ? (
          <ErrorDisplay
            errorMessage={userError?.message || "An error occurred"}
            buttonText="Try Again"
            onButtonClick={loadServicePro}
          />
        ) : (
          <>
            <div className="flex flex-col gap-6 lg:w-auto lg:min-w-0">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-[52px] items-center sm:items-start">
                <div className="w-36 h-36">
                  <ProfileImage
                    profilePicture={profile?.profilePicture}
                    firstName={profile?.firstName}
                    lastName={profile?.lastName}
                    fontSize="text-[42px]"
                    backgroundColor="#FBC948"
                  />
                </div>
                <div className="flex flex-col gap-2 items-center sm:items-start">
                  <h2 className="text-2xl sm:text-level-2 font-semibold font-nonito_sans text-primary leading-normal sm:leading-[48px] tracking-[0.032em]">
                    {profile?.firstName} {profile?.lastName}
                  </h2>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex">
                      <img
                        src={LocatioPinIcon}
                        alt="Location"
                        className="w-5 h-5"
                      />
                      <span className="text-content font-inter text-sm sm:text-level-7 font-medium leading-6">
                        {serviceAreas[0]?.cityName || "City name not available"}
                      </span>
                    </div>
                    <div className=" text-content font-inter text-level-7 font-medium leading-6 bg-gray-100 px-2 py-0 rounded-md shadow-sm">{`Total Orders: ${profile?.totolCompletedOrders ?? 0}`}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star
                      size="24"
                      className="fill-yellow-300 text-yellow-300"
                    />
                    <p className="text-content font-inter text-sm sm:text-level-7 font-medium leading-6 mb-0">
                      {profile?.avgRatingValue} ({profile?.totalReviews}{" "}
                      reviews)
                    </p>
                  </div>
                  {/* <span className="w-[127px] h-8 bg-secondary rounded-[10px] font-inter font-semibold text-sm sm:text-level-7 flex items-center justify-center text-primary">
                    Level {profile.level || 1} Seller
                  </span> */}
                </div>
              </div>
              <CustomButton
                className="bg-primary text-level-5 mt-auto text-white font-inter font-semibold h-12 w-full flex items-center justify-center rounded-xl hover:bg-opacity-90 transition-colors"
                loaderColor="white"
                loading={isCreateThread}
                buttonText="Start Chat"
                onClick={() => {
                  handleStartChat(profile?._id?.toString());
                }}
                disabled={
                  profile?._id?.toString() === userState?._id?.toString()
                }
              />
            </div>
            <div className="flex-1 flex flex-col gap-6 sm:gap-[24px]">
              <div className="flex flex-col gap-2 sm:gap-[6px]">
                <h3 className="font-inter font-medium text-base sm:text-level-5 text-dark">
                  About Me
                </h3>
                <p className="font-inter text-sm sm:text-level-6 text-content leading-6">
                  {profile?.aboutMe?.primary || "No information provided."}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:gap-[6px]">
                <h3 className="font-inter font-medium text-base sm:text-level-5 text-dark">
                  Service Areas
                </h3>
                <div className=" flex gap-[12px] flex-wrap overflow-x-auto w-full ">
                  <SlidingTabs
                    items={serviceAreas}
                    displayKey="cityName"
                    activeItem={activeLocation}
                    onItemSelect={setActiveLocation}
                    swipeEnabled={true}
                    image={false}
                  />
                </div>
                <h3 className="font-inter font-medium text-base sm:text-level-5 text-dark mt-[1.5rem]">
                  Sub City
                </h3>
                <div className="  flex flex-row gap-2 ">
                  {subCities.map((city, index) => (
                    <div
                      key={index}
                      className="bg-[#FAFAFA] w-fit rounded-[1.25rem] px-[1.25rem] py-[0.75rem] text-bold font-inter text-[0.875rem]"
                    >
                      {city}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col-reverse xl:flex-row gap-6 sm:gap-8 md:gap-10">
        {/* Left Section (Scrollable Reviews) */}
        <div
          ref={reviewsRef}
          className="w-full h-fit xl:w-[536px] xl:min-w-[536px] flex flex-col gap-6 sm:gap-9 border rounded-lg sm:rounded-[20px] border-light-blue bg-white p-4 sm:p-5 lg:p-9"
        >
          {isFetchingReviews || isFetchingUser ? (
            <div>
              <div className="flex justify-between mb-[36px]">
                <p className="font-inter font-medium text-[16px] text-[#222325] ">
                  Most Relevent
                </p>
              </div>
              <div className="flex flex-col gap-[14px]">
                {[0, 1].map((index) => (
                  <ReviewCardSkeleton key={index} />
                ))}
              </div>
            </div>
          ) : reviews.length > 0 ? (
            <>
              <div className="flex justify-between items-center font-inter font-medium text-sm sm:text-level-5 text-dark">
                <p>
                  {reviews.length} out of {totalReviews || 0} Reviews
                </p>
                <p>Most Relevant</p>
              </div>

              {/* Review Cards */}
              <div className="flex flex-col gap-[10px]">
                <div className="flex flex-col gap-[14px]">
                  {reviews.map((reviewData, index) => (
                    <ReviewCard
                      key={index}
                      customerFeedback={reviewData?.review?.customerFeedback}
                      rating={reviewData?.review?.rating || 0}
                      createdAt={reviewData?.review?.createdAt}
                      price={reviewData?.price}
                      firstName={reviewData?.userFirstName}
                      lastName={reviewData?.userLastName}
                      profilePicture={reviewData?.profilePicture}
                      role={reviewData?.userRole}
                    />
                  ))}
                </div>
                {hasMore && (
                  <CustomButton
                    onClick={handleLoadMore}
                    buttonText="Load More"
                    className="w-full h-[49px] bg-primary text-level-5 text-white font-inter font-semibold"
                    loaderColor="white"
                    loading={isFetchingReviews}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center py-8">
              <p className="font-inter font-medium text-[16px] text-[#222325] ">
                No Relevant Reviews Found
              </p>
            </div>
          )}
        </div>

        <div className="xl:block hidden w-full">
          {/* Right Section (Sticky Skills Section) */}
          <StickyBox
            offsetTop={20}
            offsetBottom={20}
            className="self-start flex-1 w-full"
          >
            <div className="border h-fit rounded-[20px] bg-white border-light-blue p-4 sm:p-5 lg:p-6">
              <h3 className="font-nonito_sans font-semibold text-xl sm:text-level-3 text-primary leading-normal sm:leading-[48px] tracking-[0.032em] text-center sm:text-left">
                Skills
              </h3>
              {isFetchingUser || isFetchingReviews ? (
                <SkillCardSkeleton />
              ) : (
                <>
                  <div className="flex gap-[12px] flex-wrap overflow-x-auto w-full">
                    <SlidingTabs
                      items={category}
                      displayKey="categoryName"
                      activeItem={activeCategory}
                      onItemSelect={setActiveCategory}
                      swipeEnabled={true}
                    />
                  </div>

                  {/* Skill Cards */}
                  <div className="mt-4 sm:mt-[24px] flex flex-col gap-4 sm:gap-6">
                    {skillsList.map((skill) => (
                      <div
                        key={skill.id}
                        className="w-full border border-light-blue rounded-lg sm:rounded-[20px] p-4 sm:p-6 flex flex-col gap-2 sm:gap-[10px]"
                      >
                        <h3 className="font-inter font-medium text-base sm:text-level-5 text-primary">
                          {skill.name}
                        </h3>
                        <p className="font-inter text-sm sm:text-level-6 text-content">
                          {skill.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </StickyBox>
        </div>

        <div className="flex-1 w-full xl:hidden block">
          <div className="border h-fit rounded-[20px] bg-white border-light-blue p-4 sm:p-5 lg:p-6">
            <h3 className="font-nonito_sans font-semibold text-xl sm:text-level-3 text-primary leading-normal sm:leading-[48px] tracking-[0.032em] text-start sm:text-left">
              Skills
            </h3>
            {
              // Loading State
              isFetchingUser || isFetchingReviews ? (
                <SkillCardSkeleton />
              ) : (
                <>
                  <div className="flex gap-[12px] flex-wrap overflow-x-auto w-full">
                    <SlidingTabs
                      items={category}
                      displayKey="categoryName"
                      activeItem={activeCategory}
                      onItemSelect={setActiveCategory}
                      swipeEnabled={true}
                    />
                  </div>

                  {/* Skill Cards */}
                  <div className="mt-4 sm:mt-[24px] flex flex-col gap-4 sm:gap-6">
                    {skillsList.map((skill) => (
                      <div
                        key={skill.id}
                        className="w-full border border-light-blue rounded-lg sm:rounded-[20px] p-4 sm:p-6 flex flex-col gap-2 sm:gap-[10px]"
                      >
                        <h3 className="font-inter font-medium text-base sm:text-level-5 text-primary">
                          {skill.name}
                        </h3>
                        <p className="font-inter text-sm sm:text-level-6 text-content">
                          {skill.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientTaskerProfile;
